/*
 * The dashboard widget settings: which widget the dashboard window displays
 * below the console, and whether the tasks list and the events timeline are
 * shown around it (the console itself is the only fixed part). Stored in the
 * browser store (localStorage), namespaced per account, which is shared
 * between the main window and the dashboard window (same origin), and
 * re-read by the dashboard window every time it is shown. The keys also
 * exist un-namespaced, written by older versions of the app; the first read
 * moves those legacy values into the reading account's keys.
 */
import { useBrowserStore } from "@/composables/useBrowserStore";

export const DASHBOARD_WIDGET_KEY = "dashboard/widget";

// "calendar" is the read-only 7-day calendar widget; it takes the whole
// dashboard width, so it supersedes the tasks list and the events timeline.
export type DashboardWidget = "none" | "journal" | "calendar";

export const DEFAULT_DASHBOARD_WIDGET: DashboardWidget = "journal";

export const isDashboardWidget = (value: unknown): value is DashboardWidget =>
  value === "none" || value === "journal" || value === "calendar";

/** Read the account's configured dashboard widget from the browser store. */
export const getDashboardWidget = (accountId: string): DashboardWidget => {
  const store = useBrowserStore(accountId);
  store.migrate(DASHBOARD_WIDGET_KEY);
  const value = store.get<unknown>(
    DASHBOARD_WIDGET_KEY,
    DEFAULT_DASHBOARD_WIDGET
  );
  return isDashboardWidget(value) ? value : DEFAULT_DASHBOARD_WIDGET;
};

// The tasks list (left of the console) and the events timeline (right of the
// console), each toggleable from Settings > Dashboard. Both shown by default.
export const DASHBOARD_TASKS_KEY = "dashboard/tasks";
export const DASHBOARD_TIMELINE_KEY = "dashboard/timeline";

export const DEFAULT_DASHBOARD_TASKS = true;
export const DEFAULT_DASHBOARD_TIMELINE = true;

const getBoolean = (
  accountId: string,
  key: string,
  defaultValue: boolean
): boolean => {
  const store = useBrowserStore(accountId);
  store.migrate(key);
  const value = store.get<unknown>(key, defaultValue);
  return typeof value === "boolean" ? value : defaultValue;
};

/** Whether the dashboard shows the tasks list. */
export const getDashboardTasks = (accountId: string): boolean =>
  getBoolean(accountId, DASHBOARD_TASKS_KEY, DEFAULT_DASHBOARD_TASKS);

/** Whether the dashboard shows the events timeline. */
export const getDashboardTimeline = (accountId: string): boolean =>
  getBoolean(accountId, DASHBOARD_TIMELINE_KEY, DEFAULT_DASHBOARD_TIMELINE);
